/**
 * Decision rules (board 12: J02 team routing, J05 rubric, J08 comparison) in the four launch
 * languages. Arabic, Hindi and Urdu are pending review by native speakers, like the rest of Studio.
 */
export const decisionsEn = {
  nav: 'Decision rules',
  title: 'Decision rules',
  description:
    'Choose which team a conversation goes to. Jev suggests a team. Fixed rules decide when Jev is not sure, fails or is too slow. A request for a person is always followed.',
  brand: 'Brand',
  noBrands: 'Add a brand first.',
  loading: 'Loading decision rules',
  noManage: 'You can look, but only administrators can change decision rules.',
  slices: {
    en: 'English',
    ar: 'Arabic',
    hi: 'Hindi',
    ur: 'Urdu',
    hinglish: 'Hinglish',
    'roman-urdu': 'Roman Urdu',
  },
  mode: {
    title: 'How routing runs',
    legend: 'Routing mode',
    off: 'Off',
    offHint: 'Conversations are not routed to teams.',
    shadow: 'Shadow',
    shadowHint:
      'Jev and the fixed rules both decide, and both are recorded. Only the fixed rules are applied.',
    live: 'Live',
    liveHint: 'Jev’s choice is applied when it is sure enough. Otherwise the fixed rules decide.',
    needsPublished: 'Publish a rubric before going live.',
    save: 'Save mode',
    saved: 'Routing mode saved.',
  },
  teams: {
    title: 'Teams',
    description:
      'Teams come from the inbox. Choose the topics and languages each team takes for this brand.',
    empty: 'No teams yet. Add one so conversations can be routed.',
    name: 'Team name',
    topics: 'Topics',
    topicsHint: 'Topic keys from the rubric, separated by commas. Example: billing, shipping',
    languages: 'Languages the team answers in',
    translationLanguages: 'Languages the team serves with translation',
    translationHint: 'The team can take conversations in these languages with translation.',
    throughTranslation: 'With translation',
    isDefault: 'Default team',
    isDefaultHint: 'Gets conversations that no other team matches.',
    defaultBadge: 'Default',
    accepting: 'Accepting conversations',
    acceptingHint: 'Turn this off to pause new conversations for this team.',
    paused: 'Paused',
    routed: 'Use this team for routing',
    routedHint: 'When this is off, routing never chooses this team for this brand.',
    notRouted: 'Not used for routing',
    allWorkspaces: 'All workspaces',
    anyTopic: 'Any topic',
    noLanguages: 'No languages set',
    add: 'Add team',
    added: 'Team added: {{name}}.',
    edit: 'Edit routing',
    editLabel: 'Edit routing for {{name}}',
    editTitle: 'Routing for {{name}}',
    save: 'Save routing',
    saved: 'Routing saved for {{name}}.',
    noPermission: 'Only people who manage teams and decision rules can add teams.',
  },
  rubric: {
    title: 'Rubric',
    description: 'The topics Jev chooses from, what it may see and how sure it must be.',
    publishedVersion: 'Published version {{version}}',
    notPublished: 'Not published yet',
    draftVersion: 'Draft version {{version}}',
    provider: 'Who suggests the team',
    providers: {
      none: 'Fixed rules only',
      mock: 'Test provider (no real model)',
      jev: 'Jev',
    },
    model: 'Pinned Jev version',
    modelHint: 'A fixed version such as jev-1.13.0. Moving names such as “latest” are not allowed.',
    jevMissing: {
      title: 'Jev is not set up on this server',
      body: 'Jev decisions will be recorded as unavailable, and the fixed rules will decide. To use Jev, the server needs a TypeSafe key:',
      guide: 'See “Decisions with Jev” in the self-hosting guide.',
    },
    deadline: 'Time limit in milliseconds',
    deadlineHint: 'After this the fixed rules decide. The chat uses 1500.',
    topics: 'Topics',
    topicKey: 'Key',
    topicKeyHint: 'Lowercase letters, digits, - or _.',
    topicDescription: 'What it covers',
    topicKeywords: 'Words for the fixed rules',
    topicKeywordsHint: 'Separated by commas. Any language.',
    topicNumber: 'Topic {{number}}',
    addTopic: 'Add topic',
    removeTopic: 'Remove topic {{number}}',
    otherNote: 'The topic “other” is added automatically.',
    inputs: 'What Jev may see',
    recentMessages: 'Recent customer messages',
    recentMessagesHint: 'Emails, phone numbers and similar details are removed first.',
    conversationStatus: 'Whether a person is already involved',
    thresholds: 'How sure Jev must be, per language',
    thresholdsHint:
      'From 0 to 1. Leave a language empty if it was not evaluated: Jev then abstains there and the fixed rules decide.',
    thresholdsEvaluated:
      'Filled in from Hamnivo’s test of {{model}}. Compare them on your own messages in shadow mode first.',
    threshold: '{{language}}',
    save: 'Save draft',
    saved: 'Draft saved.',
    publish: 'Publish draft',
    publishTitle: 'Publish this rubric?',
    publishBody:
      'Routing uses it from the next conversation. In shadow mode it is only recorded, not applied.',
    publishedNotice: 'Rubric version {{version}} is published.',
    noDraft: 'Save a draft to publish it.',
  },
  test: {
    title: 'Try a message',
    description: 'A made-up message to see what would happen. Nothing is sent to anyone.',
    message: 'Message',
    which: 'Rubric to try',
    draft: 'Draft',
    published: 'Published',
    run: 'Try it',
    result: 'What would happen',
    status: {
      decided: 'Jev decided',
      abstained: 'Jev was not sure',
      unavailable: 'Jev did not answer',
      invalid: 'Jev’s answer did not fit the rubric',
      skipped: 'Jev was not asked',
    },
    statusHint: {
      decided: 'Jev’s topic is used when the mode is live.',
      abstained: 'The fixed rules decided. No confidence was made up.',
      unavailable: 'The fixed rules decided, so the conversation was not held up.',
      invalid: 'The answer was set aside and the fixed rules decided.',
      skipped: 'Only the fixed rules are used.',
    },
    providerTopic: 'Jev’s topic',
    confidence: 'Jev’s confidence',
    noConfidence: 'Not given',
    baselineTopic: 'Fixed rules’ topic',
    finalTopic: 'Topic used',
    fromProvider: 'from Jev',
    fromBaseline: 'from the fixed rules',
    team: 'Team',
    noTeam: 'No team matches',
    why: 'Why the fixed rules decided',
    fallback: {
      not_asked: 'Jev was not asked.',
      abstained: 'Jev was not sure enough.',
      unavailable: 'Jev did not answer in time, or could not be reached.',
      invalid: 'Jev’s answer did not fit the rubric.',
      label_not_offered: 'Jev chose a topic that is not offered.',
    },
    detail: 'Details for administrators',
    time: 'Time taken',
    request: 'Request',
    explicit: 'The message asks for a person. The hand-off happens at once, whatever Jev says.',
    redacted: 'Removed before Jev saw it: {{kinds}}',
    none: '—',
  },
  records: {
    title: 'Recent decisions',
    description: 'Topics and teams only. Customer messages are not kept here.',
    empty: 'No decisions yet.',
    time: 'Time',
    trigger: 'Trigger',
    jev: 'Jev',
    used: 'Used',
    team: 'Team',
    applied: 'Applied',
    review: 'Review',
    triggers: {
      new_conversation: 'New conversation',
      handoff: 'Hand-off',
      test: 'Test',
    },
    modes: {
      shadow: 'Shadow',
      live: 'Live',
      test: 'Test',
    },
    yes: 'Yes',
    no: 'No',
    reviewed: 'Reviewed: {{topic}}',
    reviewAction: 'Review',
    reviewLabel: 'Review the decision from {{time}}',
    more: 'Show more',
  },
  review: {
    title: 'Review this decision',
    summary: 'Jev: {{provider}}. Fixed rules: {{baseline}}. Used: {{final}}.',
    topic: 'Correct topic',
    reason: 'Why',
    consent: 'This label may be used to test future versions',
    consentHint: 'Only labels with this consent are used in comparisons.',
    save: 'Save review',
    saved: 'Review saved.',
  },
  experiment: {
    title: 'Jev compared with the fixed rules',
    description: 'From recorded decisions. Accuracy counts reviewed decisions only.',
    empty: 'No decisions to compare yet.',
    language: 'Language',
    all: 'All languages',
    decisions: 'Decisions',
    agreement: 'Same topic',
    jevAccuracy: 'Jev right',
    baselineAccuracy: 'Fixed rules right',
    noAnswer: 'Jev not sure or no answer',
    time: 'Jev’s time (median / 95%)',
    tokens: 'Tokens (in / out)',
    notReviewed: 'No reviews yet',
  },
} as const;

type Strings<T> = { [K in keyof T]: T[K] extends string ? string : Strings<T[K]> };
export type DecisionsText = Strings<typeof decisionsEn>;

export const decisionsAr: DecisionsText = {
  nav: 'قواعد القرار',
  title: 'قواعد القرار',
  description:
    'اختر الفريق الذي تذهب إليه كل محادثة. يقترح Jev فريقًا. وتقرر القواعد الثابتة عندما لا يكون Jev متأكدًا أو يفشل أو يتأخر. طلب التحدث إلى شخص يُنفَّذ دائمًا.',
  brand: 'العلامة التجارية',
  noBrands: 'أضف علامة تجارية أولًا.',
  loading: 'جارٍ تحميل قواعد القرار',
  noManage: 'يمكنك الاطلاع، لكن المسؤولين وحدهم يستطيعون تغيير قواعد القرار.',
  slices: {
    en: 'الإنجليزية',
    ar: 'العربية',
    hi: 'الهندية',
    ur: 'الأردية',
    hinglish: 'الهندية بحروف لاتينية',
    'roman-urdu': 'الأردية بحروف لاتينية',
  },
  mode: {
    title: 'طريقة التوجيه',
    legend: 'وضع التوجيه',
    off: 'متوقف',
    offHint: 'لا تُوجَّه المحادثات إلى الفرق.',
    shadow: 'مقارنة فقط',
    shadowHint: 'يقرر Jev والقواعد الثابتة معًا، ويُسجَّل القراران. تُطبَّق القواعد الثابتة وحدها.',
    live: 'مُفعَّل',
    liveHint: 'يُطبَّق اختيار Jev عندما يكون متأكدًا بما يكفي. وإلا تقرر القواعد الثابتة.',
    needsPublished: 'انشر معايير القرار قبل التفعيل.',
    save: 'حفظ الوضع',
    saved: 'حُفظ وضع التوجيه.',
  },
  teams: {
    title: 'الفرق',
    description:
      'الفرق تأتي من صندوق الوارد. اختر المواضيع واللغات التي يتولاها كل فريق لهذه العلامة التجارية.',
    empty: 'لا توجد فرق بعد. أضف فريقًا لتوجيه المحادثات.',
    name: 'اسم الفريق',
    topics: 'المواضيع',
    topicsHint: 'مفاتيح المواضيع من معايير القرار، مفصولة بفواصل. مثال: billing, shipping',
    languages: 'اللغات التي يجيب بها الفريق',
    translationLanguages: 'لغات يخدمها الفريق بالترجمة',
    translationHint: 'يستطيع الفريق استلام محادثات بهذه اللغات مع الترجمة.',
    throughTranslation: 'بالترجمة',
    isDefault: 'الفريق الافتراضي',
    isDefaultHint: 'يستقبل المحادثات التي لا تناسب أي فريق آخر.',
    defaultBadge: 'افتراضي',
    accepting: 'يستقبل المحادثات',
    acceptingHint: 'أوقف هذا الخيار لإيقاف المحادثات الجديدة لهذا الفريق مؤقتًا.',
    paused: 'متوقف مؤقتًا',
    routed: 'استخدم هذا الفريق في التوجيه',
    routedHint: 'عند إيقاف هذا الخيار لا يختار التوجيه هذا الفريق لهذه العلامة التجارية.',
    notRouted: 'غير مستخدم في التوجيه',
    allWorkspaces: 'كل مساحات العمل',
    anyTopic: 'أي موضوع',
    noLanguages: 'لم تُحدَّد لغات',
    add: 'إضافة فريق',
    added: 'أُضيف الفريق: {{name}}.',
    edit: 'تعديل التوجيه',
    editLabel: 'تعديل التوجيه لفريق {{name}}',
    editTitle: 'التوجيه لفريق {{name}}',
    save: 'حفظ التوجيه',
    saved: 'حُفظ التوجيه لفريق {{name}}.',
    noPermission: 'يستطيع من يدير الفرق وقواعد القرار وحدهم إضافة الفرق.',
  },
  rubric: {
    title: 'معايير القرار',
    description: 'المواضيع التي يختار منها Jev، وما يُسمح له برؤيته، ومدى التأكد المطلوب.',
    publishedVersion: 'الإصدار المنشور {{version}}',
    notPublished: 'لم يُنشر بعد',
    draftVersion: 'المسودة، الإصدار {{version}}',
    provider: 'من يقترح الفريق',
    providers: {
      none: 'القواعد الثابتة فقط',
      mock: 'مزوّد تجريبي (بلا نموذج حقيقي)',
      jev: 'Jev',
    },
    model: 'إصدار Jev المثبّت',
    modelHint: 'إصدار ثابت مثل jev-1.13.0. لا يُسمح بأسماء متغيرة مثل “latest”.',
    jevMissing: {
      title: 'لم يُضبط Jev على هذا الخادم',
      body: 'ستُسجَّل قرارات Jev على أنها غير متاحة، وستقرر القواعد الثابتة. لاستخدام Jev، يحتاج الخادم إلى مفتاح TypeSafe:',
      guide: 'راجع “Decisions with Jev” في دليل الاستضافة الذاتية.',
    },
    deadline: 'المهلة بالمللي ثانية',
    deadlineHint: 'بعدها تقرر القواعد الثابتة. تستخدم المحادثة 1500.',
    topics: 'المواضيع',
    topicKey: 'المفتاح',
    topicKeyHint: 'حروف لاتينية صغيرة أو أرقام أو - أو _.',
    topicDescription: 'ما يشمله',
    topicKeywords: 'كلمات للقواعد الثابتة',
    topicKeywordsHint: 'مفصولة بفواصل. بأي لغة.',
    topicNumber: 'الموضوع {{number}}',
    addTopic: 'إضافة موضوع',
    removeTopic: 'إزالة الموضوع {{number}}',
    otherNote: 'يُضاف الموضوع “other” تلقائيًا.',
    inputs: 'ما يُسمح لـ Jev برؤيته',
    recentMessages: 'أحدث رسائل العميل',
    recentMessagesHint: 'تُحذف أولًا عناوين البريد وأرقام الهواتف وما يشبهها.',
    conversationStatus: 'هل يشارك شخص في المحادثة بالفعل',
    thresholds: 'مدى التأكد المطلوب من Jev لكل لغة',
    thresholdsHint:
      'من 0 إلى 1. اترك اللغة فارغة إن لم تُقيَّم: عندها يمتنع Jev فيها وتقرر القواعد الثابتة.',
    thresholdsEvaluated:
      'مأخوذة من اختبار Hamnivo للإصدار {{model}}. قارنها أولًا على رسائلك في وضع “مقارنة فقط”.',
    threshold: '{{language}}',
    save: 'حفظ المسودة',
    saved: 'حُفظت المسودة.',
    publish: 'نشر المسودة',
    publishTitle: 'نشر معايير القرار هذه؟',
    publishBody: 'يستخدمها التوجيه من المحادثة التالية. في وضع المقارنة فقط تُسجَّل ولا تُطبَّق.',
    publishedNotice: 'نُشر الإصدار {{version}} من معايير القرار.',
    noDraft: 'احفظ مسودة لتنشرها.',
  },
  test: {
    title: 'جرّب رسالة',
    description: 'رسالة مُختلَقة لترى ما سيحدث. لا يُرسَل شيء إلى أحد.',
    message: 'الرسالة',
    which: 'معايير القرار المراد تجربتها',
    draft: 'المسودة',
    published: 'المنشورة',
    run: 'جرّب',
    result: 'ما سيحدث',
    status: {
      decided: 'قرر Jev',
      abstained: 'لم يكن Jev متأكدًا',
      unavailable: 'لم يُجب Jev',
      invalid: 'إجابة Jev لا تطابق معايير القرار',
      skipped: 'لم يُسأل Jev',
    },
    statusHint: {
      decided: 'يُستخدم موضوع Jev عندما يكون الوضع مُفعَّلًا.',
      abstained: 'قررت القواعد الثابتة. لم تُختلَق أي نسبة تأكد.',
      unavailable: 'قررت القواعد الثابتة، فلم تتأخر المحادثة.',
      invalid: 'استُبعدت الإجابة وقررت القواعد الثابتة.',
      skipped: 'تُستخدم القواعد الثابتة وحدها.',
    },
    providerTopic: 'موضوع Jev',
    confidence: 'نسبة تأكد Jev',
    noConfidence: 'لم تُذكر',
    baselineTopic: 'موضوع القواعد الثابتة',
    finalTopic: 'الموضوع المستخدم',
    fromProvider: 'من Jev',
    fromBaseline: 'من القواعد الثابتة',
    team: 'الفريق',
    noTeam: 'لا يوجد فريق مناسب',
    why: 'لماذا قررت القواعد الثابتة',
    fallback: {
      not_asked: 'لم يُسأل Jev.',
      abstained: 'لم يكن Jev متأكدًا بما يكفي.',
      unavailable: 'لم يُجب Jev في الوقت المحدد، أو تعذّر الوصول إليه.',
      invalid: 'إجابة Jev لا تطابق معايير القرار.',
      label_not_offered: 'اختار Jev موضوعًا غير معروض.',
    },
    detail: 'تفاصيل للمسؤولين',
    time: 'الوقت المستغرق',
    request: 'الطلب',
    explicit: 'تطلب الرسالة التحدث إلى شخص. يتم التحويل فورًا مهما قال Jev.',
    redacted: 'حُذف قبل أن يراه Jev: {{kinds}}',
    none: '—',
  },
  records: {
    title: 'أحدث القرارات',
    description: 'المواضيع والفرق فقط. لا تُحفظ رسائل العملاء هنا.',
    empty: 'لا توجد قرارات بعد.',
    time: 'الوقت',
    trigger: 'السبب',
    jev: 'Jev',
    used: 'المستخدم',
    team: 'الفريق',
    applied: 'طُبِّق',
    review: 'المراجعة',
    triggers: {
      new_conversation: 'محادثة جديدة',
      handoff: 'تحويل إلى شخص',
      test: 'تجربة',
    },
    modes: {
      shadow: 'مقارنة فقط',
      live: 'مُفعَّل',
      test: 'تجربة',
    },
    yes: 'نعم',
    no: 'لا',
    reviewed: 'رُوجع: {{topic}}',
    reviewAction: 'مراجعة',
    reviewLabel: 'مراجعة القرار الصادر في {{time}}',
    more: 'عرض المزيد',
  },
  review: {
    title: 'مراجعة هذا القرار',
    summary: 'Jev: {{provider}}. القواعد الثابتة: {{baseline}}. المستخدم: {{final}}.',
    topic: 'الموضوع الصحيح',
    reason: 'السبب',
    consent: 'يمكن استخدام هذا التصنيف لاختبار الإصدارات القادمة',
    consentHint: 'لا تُستخدم في المقارنات إلا التصنيفات التي تحمل هذه الموافقة.',
    save: 'حفظ المراجعة',
    saved: 'حُفظت المراجعة.',
  },
  experiment: {
    title: 'مقارنة Jev بالقواعد الثابتة',
    description: 'من القرارات المسجلة. تُحسب الدقة من القرارات التي رُوجعت فقط.',
    empty: 'لا توجد قرارات للمقارنة بعد.',
    language: 'اللغة',
    all: 'كل اللغات',
    decisions: 'القرارات',
    agreement: 'الموضوع نفسه',
    jevAccuracy: 'أصاب Jev',
    baselineAccuracy: 'أصابت القواعد الثابتة',
    noAnswer: 'Jev غير متأكد أو بلا إجابة',
    time: 'وقت Jev (الوسيط / 95%)',
    tokens: 'الرموز (داخل / خارج)',
    notReviewed: 'لا مراجعات بعد',
  },
};

export const decisionsHi: DecisionsText = {
  nav: 'निर्णय के नियम',
  title: 'निर्णय के नियम',
  description:
    'चुनें कि हर बातचीत किस टीम के पास जाए। Jev टीम सुझाता है। जब Jev पक्का न हो, विफल हो या बहुत देर करे, तब तय नियम फ़ैसला करते हैं। किसी व्यक्ति से बात करने का अनुरोध हमेशा माना जाता है।',
  brand: 'ब्रांड',
  noBrands: 'पहले एक ब्रांड जोड़ें।',
  loading: 'निर्णय के नियम लोड हो रहे हैं',
  noManage: 'आप देख सकते हैं, पर निर्णय के नियम सिर्फ़ एडमिन बदल सकते हैं।',
  slices: {
    en: 'अंग्रेज़ी',
    ar: 'अरबी',
    hi: 'हिन्दी',
    ur: 'उर्दू',
    hinglish: 'हिंग्लिश',
    'roman-urdu': 'रोमन उर्दू',
  },
  mode: {
    title: 'रूटिंग कैसे चलती है',
    legend: 'रूटिंग मोड',
    off: 'बंद',
    offHint: 'बातचीत टीमों को नहीं भेजी जाती।',
    shadow: 'सिर्फ़ तुलना',
    shadowHint: 'Jev और तय नियम दोनों फ़ैसला करते हैं और दोनों दर्ज होते हैं। सिर्फ़ तय नियम लागू होते हैं।',
    live: 'लाइव',
    liveHint: 'Jev पर्याप्त पक्का हो तो उसका चुनाव लागू होता है। नहीं तो तय नियम फ़ैसला करते हैं।',
    needsPublished: 'लाइव करने से पहले निर्णय मानदंड प्रकाशित करें।',
    save: 'मोड सहेजें',
    saved: 'रूटिंग मोड सहेजा गया।',
  },
  teams: {
    title: 'टीमें',
    description: 'टीमें इनबॉक्स से आती हैं। इस ब्रांड के लिए हर टीम के विषय और भाषाएँ चुनें।',
    empty: 'अभी कोई टीम नहीं है। बातचीत भेजने के लिए एक टीम जोड़ें।',
    name: 'टीम का नाम',
    topics: 'विषय',
    topicsHint: 'निर्णय मानदंड की विषय कुंजियाँ, कॉमा से अलग। उदाहरण: billing, shipping',
    languages: 'टीम किन भाषाओं में जवाब देती है',
    translationLanguages: 'टीम अनुवाद से किन भाषाओं में मदद करती है',
    translationHint: 'टीम इन भाषाओं की बातचीत अनुवाद के साथ ले सकती है।',
    throughTranslation: 'अनुवाद के साथ',
    isDefault: 'डिफ़ॉल्ट टीम',
    isDefaultHint: 'जो बातचीत किसी और टीम से मेल न खाए, वह इसे मिलती है।',
    defaultBadge: 'डिफ़ॉल्ट',
    accepting: 'बातचीत ले रही है',
    acceptingHint: 'इस टीम को नई बातचीत कुछ समय के लिए रोकने के लिए इसे बंद करें।',
    paused: 'रुकी हुई',
    routed: 'रूटिंग में इस टीम का इस्तेमाल करें',
    routedHint: 'यह बंद हो तो रूटिंग इस ब्रांड के लिए यह टीम कभी नहीं चुनती।',
    notRouted: 'रूटिंग में इस्तेमाल नहीं',
    allWorkspaces: 'सभी वर्कस्पेस',
    anyTopic: 'कोई भी विषय',
    noLanguages: 'कोई भाषा तय नहीं',
    add: 'टीम जोड़ें',
    added: 'टीम जोड़ी गई: {{name}}।',
    edit: 'रूटिंग बदलें',
    editLabel: '{{name}} की रूटिंग बदलें',
    editTitle: '{{name}} की रूटिंग',
    save: 'रूटिंग सहेजें',
    saved: '{{name}} की रूटिंग सहेजी गई।',
    noPermission: 'टीमें सिर्फ़ वे लोग जोड़ सकते हैं जो टीमें और निर्णय के नियम संभालते हैं।',
  },
  rubric: {
    title: 'निर्णय मानदंड',
    description: 'Jev किन विषयों में से चुनता है, क्या देख सकता है और उसे कितना पक्का होना चाहिए।',
    publishedVersion: 'प्रकाशित संस्करण {{version}}',
    notPublished: 'अभी प्रकाशित नहीं',
    draftVersion: 'ड्राफ़्ट संस्करण {{version}}',
    provider: 'टीम कौन सुझाता है',
    providers: {
      none: 'सिर्फ़ तय नियम',
      mock: 'परीक्षण प्रदाता (असली मॉडल नहीं)',
      jev: 'Jev',
    },
    model: 'Jev का तय संस्करण',
    modelHint: 'एक तय संस्करण, जैसे jev-1.13.0। “latest” जैसे बदलते नाम की अनुमति नहीं है।',
    jevMissing: {
      title: 'इस सर्वर पर Jev सेट नहीं है',
      body: 'Jev के फ़ैसले उपलब्ध नहीं के रूप में दर्ज होंगे, और तय नियम फ़ैसला करेंगे। Jev इस्तेमाल करने के लिए सर्वर को TypeSafe कुंजी चाहिए:',
      guide: 'सेल्फ़-होस्टिंग गाइड में “Decisions with Jev” देखें।',
    },
    deadline: 'समय सीमा (मिलीसेकंड)',
    deadlineHint: 'इसके बाद तय नियम फ़ैसला करते हैं। चैट 1500 इस्तेमाल करती है।',
    topics: 'विषय',
    topicKey: 'कुंजी',
    topicKeyHint: 'छोटे लैटिन अक्षर, अंक, - या _।',
    topicDescription: 'इसमें क्या आता है',
    topicKeywords: 'तय नियमों के लिए शब्द',
    topicKeywordsHint: 'कॉमा से अलग। किसी भी भाषा में।',
    topicNumber: 'विषय {{number}}',
    addTopic: 'विषय जोड़ें',
    removeTopic: 'विषय {{number}} हटाएँ',
    otherNote: '“other” विषय अपने आप जुड़ जाता है।',
    inputs: 'Jev क्या देख सकता है',
    recentMessages: 'ग्राहक के हाल के संदेश',
    recentMessagesHint: 'ईमेल, फ़ोन नंबर और ऐसी जानकारी पहले हटा दी जाती है।',
    conversationStatus: 'क्या कोई व्यक्ति पहले से बातचीत में है',
    thresholds: 'हर भाषा में Jev को कितना पक्का होना चाहिए',
    thresholdsHint:
      '0 से 1 तक। जिस भाषा का मूल्यांकन नहीं हुआ, उसे खाली छोड़ें: वहाँ Jev फ़ैसला नहीं करेगा और तय नियम फ़ैसला करेंगे।',
    thresholdsEvaluated:
      'Hamnivo के {{model}} के परीक्षण से भरे गए। पहले अपने संदेशों पर “सिर्फ़ तुलना” मोड में जाँचें।',
    threshold: '{{language}}',
    save: 'ड्राफ़्ट सहेजें',
    saved: 'ड्राफ़्ट सहेजा गया।',
    publish: 'ड्राफ़्ट प्रकाशित करें',
    publishTitle: 'यह निर्णय मानदंड प्रकाशित करें?',
    publishBody:
      'अगली बातचीत से रूटिंग इसे इस्तेमाल करेगी। “सिर्फ़ तुलना” मोड में यह सिर्फ़ दर्ज होता है, लागू नहीं।',
    publishedNotice: 'निर्णय मानदंड का संस्करण {{version}} प्रकाशित हुआ।',
    noDraft: 'प्रकाशित करने के लिए पहले ड्राफ़्ट सहेजें।',
  },
  test: {
    title: 'एक संदेश आज़माएँ',
    description: 'एक बनावटी संदेश, यह देखने के लिए कि क्या होगा। किसी को कुछ नहीं भेजा जाता।',
    message: 'संदेश',
    which: 'कौन सा निर्णय मानदंड आज़माएँ',
    draft: 'ड्राफ़्ट',
    published: 'प्रकाशित',
    run: 'आज़माएँ',
    result: 'क्या होगा',
    status: {
      decided: 'Jev ने फ़ैसला किया',
      abstained: 'Jev पक्का नहीं था',
      unavailable: 'Jev ने जवाब नहीं दिया',
      invalid: 'Jev का जवाब निर्णय मानदंड से मेल नहीं खाया',
      skipped: 'Jev से पूछा नहीं गया',
    },
    statusHint: {
      decided: 'लाइव मोड में Jev का विषय इस्तेमाल होता है।',
      abstained: 'तय नियमों ने फ़ैसला किया। कोई भरोसा-स्तर गढ़ा नहीं गया।',
      unavailable: 'तय नियमों ने फ़ैसला किया, इसलिए बातचीत रुकी नहीं।',
      invalid: 'जवाब अलग रखा गया और तय नियमों ने फ़ैसला किया।',
      skipped: 'सिर्फ़ तय नियम इस्तेमाल होते हैं।',
    },
    providerTopic: 'Jev का विषय',
    confidence: 'Jev का भरोसा',
    noConfidence: 'नहीं बताया',
    baselineTopic: 'तय नियमों का विषय',
    finalTopic: 'इस्तेमाल हुआ विषय',
    fromProvider: 'Jev से',
    fromBaseline: 'तय नियमों से',
    team: 'टीम',
    noTeam: 'कोई टीम मेल नहीं खाती',
    why: 'तय नियमों ने फ़ैसला क्यों किया',
    fallback: {
      not_asked: 'Jev से पूछा नहीं गया।',
      abstained: 'Jev पर्याप्त पक्का नहीं था।',
      unavailable: 'Jev ने समय पर जवाब नहीं दिया, या उस तक पहुँचा नहीं जा सका।',
      invalid: 'Jev का जवाब निर्णय मानदंड से मेल नहीं खाया।',
      label_not_offered: 'Jev ने ऐसा विषय चुना जो दिया ही नहीं गया था।',
    },
    detail: 'एडमिन के लिए विवरण',
    time: 'लगा समय',
    request: 'अनुरोध',
    explicit: 'संदेश किसी व्यक्ति से बात करना चाहता है। Jev कुछ भी कहे, बातचीत तुरंत सौंपी जाती है।',
    redacted: 'Jev के देखने से पहले हटाया गया: {{kinds}}',
    none: '—',
  },
  records: {
    title: 'हाल के फ़ैसले',
    description: 'सिर्फ़ विषय और टीमें। ग्राहकों के संदेश यहाँ नहीं रखे जाते।',
    empty: 'अभी कोई फ़ैसला नहीं।',
    time: 'समय',
    trigger: 'वजह',
    jev: 'Jev',
    used: 'इस्तेमाल हुआ',
    team: 'टीम',
    applied: 'लागू',
    review: 'समीक्षा',
    triggers: {
      new_conversation: 'नई बातचीत',
      handoff: 'व्यक्ति को सौंपना',
      test: 'परीक्षण',
    },
    modes: {
      shadow: 'सिर्फ़ तुलना',
      live: 'लाइव',
      test: 'परीक्षण',
    },
    yes: 'हाँ',
    no: 'नहीं',
    reviewed: 'समीक्षा हुई: {{topic}}',
    reviewAction: 'समीक्षा करें',
    reviewLabel: '{{time}} के फ़ैसले की समीक्षा करें',
    more: 'और दिखाएँ',
  },
  review: {
    title: 'इस फ़ैसले की समीक्षा करें',
    summary: 'Jev: {{provider}}। तय नियम: {{baseline}}। इस्तेमाल हुआ: {{final}}।',
    topic: 'सही विषय',
    reason: 'क्यों',
    consent: 'यह लेबल आगे के संस्करणों को परखने में इस्तेमाल हो सकता है',
    consentHint: 'तुलना में सिर्फ़ इसी सहमति वाले लेबल इस्तेमाल होते हैं।',
    save: 'समीक्षा सहेजें',
    saved: 'समीक्षा सहेजी गई।',
  },
  experiment: {
    title: 'Jev और तय नियमों की तुलना',
    description: 'दर्ज फ़ैसलों से। सटीकता सिर्फ़ समीक्षा किए गए फ़ैसलों से गिनी जाती है।',
    empty: 'तुलना के लिए अभी कोई फ़ैसला नहीं।',
    language: 'भाषा',
    all: 'सभी भाषाएँ',
    decisions: 'फ़ैसले',
    agreement: 'एक ही विषय',
    jevAccuracy: 'Jev सही',
    baselineAccuracy: 'तय नियम सही',
    noAnswer: 'Jev पक्का नहीं या जवाब नहीं',
    time: 'Jev का समय (माध्यिका / 95%)',
    tokens: 'टोकन (अंदर / बाहर)',
    notReviewed: 'अभी कोई समीक्षा नहीं',
  },
};

export const decisionsUr: DecisionsText = {
  nav: 'فیصلے کے اصول',
  title: 'فیصلے کے اصول',
  description:
    'چنیں کہ ہر گفتگو کس ٹیم کے پاس جائے۔ Jev ٹیم تجویز کرتا ہے۔ جب Jev کو یقین نہ ہو، وہ ناکام ہو یا بہت دیر کرے تو طے شدہ اصول فیصلہ کرتے ہیں۔ کسی شخص سے بات کرنے کی درخواست ہمیشہ مانی جاتی ہے۔',
  brand: 'برانڈ',
  noBrands: 'پہلے ایک برانڈ شامل کریں۔',
  loading: 'فیصلے کے اصول لوڈ ہو رہے ہیں',
  noManage: 'آپ دیکھ سکتے ہیں، لیکن فیصلے کے اصول صرف منتظمین بدل سکتے ہیں۔',
  slices: {
    en: 'انگریزی',
    ar: 'عربی',
    hi: 'ہندی',
    ur: 'اردو',
    hinglish: 'ہنگلش',
    'roman-urdu': 'رومن اردو',
  },
  mode: {
    title: 'روٹنگ کیسے چلتی ہے',
    legend: 'روٹنگ موڈ',
    off: 'بند',
    offHint: 'گفتگو ٹیموں کو نہیں بھیجی جاتی۔',
    shadow: 'صرف موازنہ',
    shadowHint:
      'Jev اور طے شدہ اصول دونوں فیصلہ کرتے ہیں اور دونوں درج ہوتے ہیں۔ صرف طے شدہ اصول لاگو ہوتے ہیں۔',
    live: 'فعال',
    liveHint: 'Jev کو کافی یقین ہو تو اس کا انتخاب لاگو ہوتا ہے۔ ورنہ طے شدہ اصول فیصلہ کرتے ہیں۔',
    needsPublished: 'فعال کرنے سے پہلے فیصلے کا معیار شائع کریں۔',
    save: 'موڈ محفوظ کریں',
    saved: 'روٹنگ موڈ محفوظ ہو گیا۔',
  },
  teams: {
    title: 'ٹیمیں',
    description: 'ٹیمیں ان باکس سے آتی ہیں۔ اس برانڈ کے لیے ہر ٹیم کے موضوعات اور زبانیں چنیں۔',
    empty: 'ابھی کوئی ٹیم نہیں۔ گفتگو بھیجنے کے لیے ایک ٹیم شامل کریں۔',
    name: 'ٹیم کا نام',
    topics: 'موضوعات',
    topicsHint: 'فیصلے کے معیار کی موضوع کلیدیں، کوما سے الگ۔ مثال: billing, shipping',
    languages: 'ٹیم کن زبانوں میں جواب دیتی ہے',
    translationLanguages: 'ٹیم ترجمے کے ساتھ کن زبانوں میں مدد کرتی ہے',
    translationHint: 'ٹیم ان زبانوں کی گفتگو ترجمے کے ساتھ لے سکتی ہے۔',
    throughTranslation: 'ترجمے کے ساتھ',
    isDefault: 'بنیادی ٹیم',
    isDefaultHint: 'جو گفتگو کسی اور ٹیم سے میل نہ کھائے وہ اسے ملتی ہے۔',
    defaultBadge: 'بنیادی',
    accepting: 'گفتگو لے رہی ہے',
    acceptingHint: 'اس ٹیم کے لیے نئی گفتگو کچھ دیر روکنے کے لیے اسے بند کریں۔',
    paused: 'رکی ہوئی',
    routed: 'روٹنگ میں یہ ٹیم استعمال کریں',
    routedHint: 'یہ بند ہو تو روٹنگ اس برانڈ کے لیے یہ ٹیم کبھی نہیں چنتی۔',
    notRouted: 'روٹنگ میں استعمال نہیں',
    allWorkspaces: 'تمام ورک اسپیس',
    anyTopic: 'کوئی بھی موضوع',
    noLanguages: 'کوئی زبان طے نہیں',
    add: 'ٹیم شامل کریں',
    added: 'ٹیم شامل ہو گئی: {{name}}۔',
    edit: 'روٹنگ بدلیں',
    editLabel: '{{name}} کی روٹنگ بدلیں',
    editTitle: '{{name}} کی روٹنگ',
    save: 'روٹنگ محفوظ کریں',
    saved: '{{name}} کی روٹنگ محفوظ ہو گئی۔',
    noPermission: 'ٹیمیں صرف وہ لوگ شامل کر سکتے ہیں جو ٹیمیں اور فیصلے کے اصول سنبھالتے ہیں۔',
  },
  rubric: {
    title: 'فیصلے کا معیار',
    description: 'Jev کن موضوعات میں سے چنتا ہے، کیا دیکھ سکتا ہے اور اسے کتنا یقین ہونا چاہیے۔',
    publishedVersion: 'شائع شدہ ورژن {{version}}',
    notPublished: 'ابھی شائع نہیں ہوا',
    draftVersion: 'مسودہ، ورژن {{version}}',
    provider: 'ٹیم کون تجویز کرتا ہے',
    providers: {
      none: 'صرف طے شدہ اصول',
      mock: 'آزمائشی فراہم کنندہ (اصل ماڈل نہیں)',
      jev: 'Jev',
    },
    model: 'Jev کا طے شدہ ورژن',
    modelHint: 'ایک طے شدہ ورژن، جیسے jev-1.13.0۔ “latest” جیسے بدلتے نام کی اجازت نہیں۔',
    jevMissing: {
      title: 'اس سرور پر Jev سیٹ نہیں ہے',
      body: 'Jev کے فیصلے دستیاب نہیں کے طور پر درج ہوں گے، اور طے شدہ اصول فیصلہ کریں گے۔ Jev استعمال کرنے کے لیے سرور کو TypeSafe کلید چاہیے:',
      guide: 'سیلف ہوسٹنگ گائیڈ میں “Decisions with Jev” دیکھیں۔',
    },
    deadline: 'وقت کی حد (ملی سیکنڈ)',
    deadlineHint: 'اس کے بعد طے شدہ اصول فیصلہ کرتے ہیں۔ چیٹ 1500 استعمال کرتی ہے۔',
    topics: 'موضوعات',
    topicKey: 'کلید',
    topicKeyHint: 'چھوٹے لاطینی حروف، ہندسے، - یا _۔',
    topicDescription: 'اس میں کیا آتا ہے',
    topicKeywords: 'طے شدہ اصولوں کے لیے الفاظ',
    topicKeywordsHint: 'کوما سے الگ۔ کسی بھی زبان میں۔',
    topicNumber: 'موضوع {{number}}',
    addTopic: 'موضوع شامل کریں',
    removeTopic: 'موضوع {{number}} ہٹائیں',
    otherNote: 'موضوع “other” خود بخود شامل ہو جاتا ہے۔',
    inputs: 'Jev کیا دیکھ سکتا ہے',
    recentMessages: 'گاہک کے حالیہ پیغامات',
    recentMessagesHint: 'ای میل، فون نمبر اور اس طرح کی تفصیلات پہلے ہٹا دی جاتی ہیں۔',
    conversationStatus: 'کیا کوئی شخص پہلے سے گفتگو میں ہے',
    thresholds: 'ہر زبان میں Jev کو کتنا یقین ہونا چاہیے',
    thresholdsHint:
      '0 سے 1 تک۔ جس زبان کی جانچ نہیں ہوئی اسے خالی چھوڑیں: وہاں Jev فیصلہ نہیں کرے گا اور طے شدہ اصول فیصلہ کریں گے۔',
    thresholdsEvaluated:
      'Hamnivo کے {{model}} کے ٹیسٹ سے بھرے گئے۔ پہلے اپنے پیغامات پر “صرف موازنہ” موڈ میں جانچیں۔',
    threshold: '{{language}}',
    save: 'مسودہ محفوظ کریں',
    saved: 'مسودہ محفوظ ہو گیا۔',
    publish: 'مسودہ شائع کریں',
    publishTitle: 'یہ فیصلے کا معیار شائع کریں؟',
    publishBody:
      'اگلی گفتگو سے روٹنگ اسے استعمال کرے گی۔ “صرف موازنہ” موڈ میں یہ صرف درج ہوتا ہے، لاگو نہیں۔',
    publishedNotice: 'فیصلے کے معیار کا ورژن {{version}} شائع ہو گیا۔',
    noDraft: 'شائع کرنے کے لیے پہلے مسودہ محفوظ کریں۔',
  },
  test: {
    title: 'ایک پیغام آزمائیں',
    description: 'ایک فرضی پیغام، یہ دیکھنے کے لیے کہ کیا ہوگا۔ کسی کو کچھ نہیں بھیجا جاتا۔',
    message: 'پیغام',
    which: 'کون سا فیصلے کا معیار آزمائیں',
    draft: 'مسودہ',
    published: 'شائع شدہ',
    run: 'آزمائیں',
    result: 'کیا ہوگا',
    status: {
      decided: 'Jev نے فیصلہ کیا',
      abstained: 'Jev کو یقین نہیں تھا',
      unavailable: 'Jev نے جواب نہیں دیا',
      invalid: 'Jev کا جواب فیصلے کے معیار سے میل نہیں کھاتا تھا',
      skipped: 'Jev سے پوچھا نہیں گیا',
    },
    statusHint: {
      decided: 'فعال موڈ میں Jev کا موضوع استعمال ہوتا ہے۔',
      abstained: 'طے شدہ اصولوں نے فیصلہ کیا۔ کوئی یقین کی شرح گھڑی نہیں گئی۔',
      unavailable: 'طے شدہ اصولوں نے فیصلہ کیا، اس لیے گفتگو رکی نہیں۔',
      invalid: 'جواب الگ رکھا گیا اور طے شدہ اصولوں نے فیصلہ کیا۔',
      skipped: 'صرف طے شدہ اصول استعمال ہوتے ہیں۔',
    },
    providerTopic: 'Jev کا موضوع',
    confidence: 'Jev کا یقین',
    noConfidence: 'نہیں بتایا',
    baselineTopic: 'طے شدہ اصولوں کا موضوع',
    finalTopic: 'استعمال شدہ موضوع',
    fromProvider: 'Jev سے',
    fromBaseline: 'طے شدہ اصولوں سے',
    team: 'ٹیم',
    noTeam: 'کوئی ٹیم میل نہیں کھاتی',
    why: 'طے شدہ اصولوں نے فیصلہ کیوں کیا',
    fallback: {
      not_asked: 'Jev سے پوچھا نہیں گیا۔',
      abstained: 'Jev کو کافی یقین نہیں تھا۔',
      unavailable: 'Jev نے وقت پر جواب نہیں دیا، یا اس تک پہنچا نہیں جا سکا۔',
      invalid: 'Jev کا جواب فیصلے کے معیار سے میل نہیں کھاتا تھا۔',
      label_not_offered: 'Jev نے ایسا موضوع چنا جو پیش ہی نہیں کیا گیا تھا۔',
    },
    detail: 'منتظمین کے لیے تفصیلات',
    time: 'لگا وقت',
    request: 'درخواست',
    explicit: 'پیغام کسی شخص سے بات کرنا چاہتا ہے۔ Jev کچھ بھی کہے، گفتگو فوراً سونپی جاتی ہے۔',
    redacted: 'Jev کے دیکھنے سے پہلے ہٹایا گیا: {{kinds}}',
    none: '—',
  },
  records: {
    title: 'حالیہ فیصلے',
    description: 'صرف موضوعات اور ٹیمیں۔ گاہکوں کے پیغامات یہاں نہیں رکھے جاتے۔',
    empty: 'ابھی کوئی فیصلہ نہیں۔',
    time: 'وقت',
    trigger: 'وجہ',
    jev: 'Jev',
    used: 'استعمال شدہ',
    team: 'ٹیم',
    applied: 'لاگو',
    review: 'جائزہ',
    triggers: {
      new_conversation: 'نئی گفتگو',
      handoff: 'شخص کو سونپنا',
      test: 'آزمائش',
    },
    modes: {
      shadow: 'صرف موازنہ',
      live: 'فعال',
      test: 'آزمائش',
    },
    yes: 'ہاں',
    no: 'نہیں',
    reviewed: 'جائزہ ہو گیا: {{topic}}',
    reviewAction: 'جائزہ لیں',
    reviewLabel: '{{time}} کے فیصلے کا جائزہ لیں',
    more: 'مزید دکھائیں',
  },
  review: {
    title: 'اس فیصلے کا جائزہ لیں',
    summary: 'Jev: {{provider}}۔ طے شدہ اصول: {{baseline}}۔ استعمال شدہ: {{final}}۔',
    topic: 'درست موضوع',
    reason: 'کیوں',
    consent: 'یہ لیبل آئندہ ورژنز کو جانچنے میں استعمال ہو سکتا ہے',
    consentHint: 'موازنوں میں صرف اسی رضامندی والے لیبل استعمال ہوتے ہیں۔',
    save: 'جائزہ محفوظ کریں',
    saved: 'جائزہ محفوظ ہو گیا۔',
  },
  experiment: {
    title: 'Jev کا طے شدہ اصولوں سے موازنہ',
    description: 'درج فیصلوں سے۔ درستی صرف جائزہ شدہ فیصلوں سے گنی جاتی ہے۔',
    empty: 'موازنے کے لیے ابھی کوئی فیصلہ نہیں۔',
    language: 'زبان',
    all: 'تمام زبانیں',
    decisions: 'فیصلے',
    agreement: 'ایک ہی موضوع',
    jevAccuracy: 'Jev درست',
    baselineAccuracy: 'طے شدہ اصول درست',
    noAnswer: 'Jev کو یقین نہیں یا جواب نہیں',
    time: 'Jev کا وقت (وسطانیہ / 95%)',
    tokens: 'ٹوکن (اندر / باہر)',
    notReviewed: 'ابھی کوئی جائزہ نہیں',
  },
};
